// Shop sensors: the reads as hooks, shared by the desktop page and the phone screen.
//   useSensorList()  GET /sensors now and every 60 s while the tab is visible (paused when hidden).
//   useSensorReadings(id, hours, stamp)  the chart's data; re-read when `stamp` changes (the list's
//     last reading / last alert) or on reload(), keeping the drawn chart while the new answer is on its way.

import { useCallback, useEffect, useRef, useState } from 'react';
import { usePolling } from '../../components/cameras/useCameraData';
import { loadSensorReadings, loadSensors } from './sensorData';
import { SENSORS_POLL_MS } from './sensorRules';

export function useSensorList() {
  const [sensors, setSensors] = useState(null); // null = loading
  const [serverTime, setServerTime] = useState(null);
  const [error, setError] = useState(null);
  const live = useRef(true);
  useEffect(() => () => { live.current = false; }, []);

  const load = useCallback(async () => {
    const r = await loadSensors();
    if (!live.current) return;
    if (r.ok && r.body && Array.isArray(r.body.sensors)) {
      setSensors(r.body.sensors);
      setServerTime(r.body.server_time || null);
      setError(null);
    } else {
      // Keep the last list on screen; say why it is not fresh.
      setError(r.message || 'Could not read the sensors.');
      setSensors((prev) => prev || []);
    }
  }, []);

  usePolling(load, SENSORS_POLL_MS, true);
  return { sensors, serverTime, error, reload: load };
}

export function useSensorReadings(sensorId, hours, stamp) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const key = `${sensorId}:${hours}`;
  const shownKey = useRef(null);

  useEffect(() => {
    if (sensorId === null || sensorId === undefined) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    // Another sensor or range: never draw the old one's readings under the new title.
    if (shownKey.current !== key) {
      setData(null);
      setError(null);
    }
    loadSensorReadings(sensorId, hours).then((r) => {
      if (cancelled) return;
      if (r.ok && r.body) {
        shownKey.current = key;
        setData(r.body);
        setError(null);
      } else {
        setError(r.message || 'Could not read the readings.');
      }
    });
    return () => { cancelled = true; };
  }, [sensorId, hours, stamp, key, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  const shown = data && shownKey.current === key ? data : null;
  return { data: shown, error, loading: !shown && !error, reload };
}
